import { Button, Card, CardContent, Input, toast } from '@app/ui';
import { FadeIn, PageTransition } from '@app/ui/motion';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { RouteErrorComponent } from '../../components/route-error';
import { client } from '../../lib/api';

export const Route = createFileRoute('/admin/support')({
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: Support,
});

function Support() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    id: string;
    name: string;
    email: string;
    sport: string | null;
    school: string | null;
    dealCount: number;
  }> | null>(null);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const result = await client.admin.users({ search: searchQuery, page: 1, limit: 10 });
      setSearchResults(result.users);
    } catch (error) {
      console.error(error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <PageTransition>
      <h1 className="text-2xl font-bold">Support</h1>
      <p className="mt-2 text-muted-foreground">Customer service tools and user impersonation.</p>

      <Card className="mt-6">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold mb-4">User Lookup</h2>
          <div className="flex gap-2">
            <Input
              type="search"
              placeholder="Search by name or email..."
              className="flex-1"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} disabled={searching}>
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </div>

          {searchResults !== null && (
            <FadeIn>
              <div className="mt-4">
                {searchResults.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4">No users found.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-2 pr-4">Name</th>
                        <th className="py-2 pr-4">Email</th>
                        <th className="py-2 pr-4">Sport</th>
                        <th className="py-2 pr-4">Deals</th>
                        <th className="py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((user) => (
                        <tr key={user.id} className="border-b hover:bg-muted/50">
                          <td className="py-2 pr-4">{user.name}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{user.email}</td>
                          <td className="py-2 pr-4">{user.sport || '--'}</td>
                          <td className="py-2 pr-4">{user.dealCount}</td>
                          <td className="py-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                toast.info(`Impersonation started for user ${user.id}`)
                              }
                            >
                              Impersonate
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </FadeIn>
          )}
        </CardContent>
      </Card>
    </PageTransition>
  );
}
